import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CultivoViewModel } from '../../../cultivos/cultivo.types';
import { FormGroup } from '@angular/forms';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { CodigoConfeccionItemViewModel } from 'src/app/components/_mantenimiento/registros/codigosconfeccion/codigoconfeccion.types';
import { TwAforoCultivoCamViewModel, TwCorteCultivoCamViewModel } from '../../unidad-inspeccion-datosbasicos.types';
import { combineLatest, map, Subject } from 'rxjs';

@Component({
    selector: 'app-inspeccion-ui-aforo-corte-edit',
    templateUrl: './inspeccion-ui-aforo-corte-edit.component.html',
    styleUrls: ['./inspeccion-ui-aforo-corte-edit.component.scss'],
})
export class InspeccionUiAforoCorteEditComponent extends ComponentBase implements OnInit, OnChanges {
    @Input() corte: TwCorteCultivoCamViewModel;
    @Input() cultivos: CultivoViewModel[];
    @Input() almacenes: CodigoConfeccionItemViewModel[] = [];
    @Input() aforosCultivos: TwAforoCultivoCamViewModel[];
    @Input() formGroup: FormGroup;
    @Input() readonly: boolean;
    @Input() fechaFinPeriodo: Date;
    @Input() fechaInicioExportacion: Date;
    @Input() habilitadaExportacion: boolean;
    @Output() cultivoSelected = new EventEmitter<CultivoViewModel>();
    @Output() aforoCultivoSelected = new EventEmitter<TwAforoCultivoCamViewModel>();

    isCollapsedCabecera = false;
    isCollapsedAforo = false;
    caducidadMinimumDate = new Date(); // La fecha mínima que aparecerá en el input calendario para la indicar la fecha de caducidad.

    private corteCargado$: Subject<boolean> = new Subject();
    private cultivosCargados$: Subject<boolean> = new Subject();

    vm$ = combineLatest([this.corteCargado$, this.cultivosCargados$])
        .pipe(
            map(([corteCargado, cultivosCargados]) => {
                if (corteCargado && cultivosCargados) {
                    this.updateForm();
                }
                return {};
            }),
        )
        .subscribe();

    constructor(
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.updateForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.corte?.currentValue) {
            if (this.corte) {
                this.corteCargado$.next(true);
            }
        }
        if (changes?.cultivos?.currentValue) {
            if (this.cultivos.length > 0) {
                // Solo necesitamos cargar éste desplegable en modo edición
                this.cultivosCargados$.next(true);
            }
        }
        if (changes?.aforosCultivos?.currentValue) {
            if (!this.corte) {
                // En modo creación (el objeto corte no existe) no disparamos éste método.
                return;
            }
            if (this.aforosCultivos.length > 0) {
                // Solo necesitamos cargar éste desplegable en modo edición
                this.loadDependenOnAforos();
            }
        }
        if (changes?.fechaFinPeriodo?.currentValue) {
            this.setFechaFinPeriodo();
        }
        if (changes?.fechaInicioExportacion?.currentValue) {
            this.setFechaInicioExportacion();
        }
    }

    private updateForm(): void {
        this.formGroup.patchValue({
            Id: this.corte?.Id || null,
            IdCultivo: this.corte?.IdCultivo || null,
            IdAforo: this.corte?.IdAforoCultivo || null, // Bolsa Productiva
            CodigoSigPacRecinto: '', // A partir del corte, obtenemos la variedad y rellenamos éste campo
            IdentificadorCultivo: '', // A partir del corte, obtenemos la variedad y rellenamos éste campo
            SuperficieVariedad: 0, // A partir del corte, obtenemos la variedad y rellenamos éste campo
            ProduccionEstimada: 0, // A partir del corte, obtenemos la variedad y rellenamos éste campo
            Corte: this.corte?.KgsAutorizados || null,
            IdAlmacen: this.corte?.IdAlmacen || null,
            Exportador: this.corte?.Exportador || '',
            FechaCaducidad: this.corte?.FechaCaducidad ? new Date(this.corte?.FechaCaducidad) : null,
            // Datos del aforo
            ProduccionAforada: 0, // A partir del corte, obtenemos el aforo y rellenamos éste campo
            ProduccionReal: 0, // A partir del corte, obtenemos el aforo y rellenamos éste campo
            KgsAcumuladosCorte: 0,
            // Datos para controlar la fecha de caducidad
            FechaInicioExportacion: this.fechaInicioExportacion,
            FechaFinPeriodo: this.fechaFinPeriodo ? new Date(this.fechaFinPeriodo) : null,
        });

        this.form = this.formGroup; // componentBase uses this.form
        this.loadDependendOnCorte();
    }

    private setFechaInicioExportacion(): void {
        this.formGroup.patchValue({
            FechaInicioExportacion: this.fechaInicioExportacion,
        });
    }

    private setFechaFinPeriodo(): void {
        this.formGroup.patchValue({
            FechaFinPeriodo: this.fechaFinPeriodo ? new Date(this.fechaFinPeriodo) : null,
        });
    }

    /**
     * Así rellenamos los campos dependendientes del cultivo y cargamos los desplegables almacenes y aforos que dependen del cultivo asociado al corte.
     * Útil cuando se edita el corte.
     * @returns void
     */
    private loadDependendOnCorte(): void {
        if (!this.corte || !this.cultivos || this.cultivos.length === 0) {
            return;
        }

        const cultivoSelected = this.cultivos.find(c => c.Id === this.corte.IdCultivo);
        if (!cultivoSelected) {
            return;
        }
        this.formGroup.patchValue({
            CodigoSigPacRecinto: cultivoSelected?.CodigoSigPacRecinto,
            IdentificadorCultivo: cultivoSelected?.Identificador,
            SuperficieVariedad: cultivoSelected?.SuperficieVariedad || null,
            ProduccionEstimada: cultivoSelected?.ProduccionEstimada || null,
        });
        this.cultivoSelected.emit(cultivoSelected);
    }

    private loadDependenOnAforos(): void {
        if (!this.aforosCultivos || this.aforosCultivos.length === 0) {
            return;
        }
        const aforoSelected = this.aforosCultivos.find(a => a.Id === this.corte.IdAforoCultivo);
        if (!this.aforosCultivos) {
            return;
        }
        this.onAforoCultivoSelected(aforoSelected!);
    }

    onAforoCultivoSelected(item: TwAforoCultivoCamViewModel): void {
        this.formGroup.patchValue({
            ProduccionAforada: item?.KgsProduccionEstimada || 0,
            ProduccionReal: item?.KgsProduccionReal || 0,
            KgsAcumuladosCorte: item?.KgsAcumuladosCorte || 0,
        });
        this.aforoCultivoSelected.emit(item);
    }

    onCultivoSelected(item: CultivoViewModel): void {
        this.formGroup.patchValue({
            IdAforo: null,
            CodigoSigPacRecinto: item?.CodigoSigPacRecinto || '',
            IdentificadorCultivo: item?.Identificador || '',
            SuperficieVariedad: item?.SuperficieVariedad || null,
            ProduccionEstimada: item?.ProduccionEstimada || null,
            Corte: null,
            IdAlmacen: null,
            Exportador: '',
            FechaCaducidad: null,
            // Datos del aforo
            ProduccionAforada: null,
            ProduccionReal: null,
            KgsAcumuladosCorte: null,
        });

        this.cultivoSelected.emit(item);
    }

    get title(): string {
        return this.corte ? (this.readonly ? 'Detalle del ' : 'Editar') : 'Nuevo'; // work mode: readonly, edition or creation
    }
}
